// GantMultiResolver.jsx
import React from 'react';
import {
  AlertTriangle,
  CalendarPlus,
  CalendarClock,
  ArrowRightLeft,
  RotateCcw,
  X,
} from 'lucide-react';

const FIVE_DAYS_MS = 5 * 24 * 60 * 60 * 1000;
const TOLERANCE_MS = 60 * 1000; // 1 min

// Sztywne pule wymiennych procesorów wg proces_nazwa_id
export const ALLOWED_PROCESSORS_BY_PROCES_NAZWA_ID = {
  1: [1, 2, 3], // Druk
  3: [14, 15, 16, 17, 18, 19, 20, 21], // Falcowanie
};

export const getProcesNazwaId = (obj) => {
  if (!obj) return null;
  const raw = obj.proces_nazwa_id ?? obj.nazwa_proces_id;
  return raw !== undefined && raw !== null && raw !== '' ? Number(raw) : null;
};

/**
 * Zwraca tablicę dozwolonych ID procesorów dla danego klocka
 * lub pustą tablicę, jeśli dla tego proces_nazwa_id zmiana maszyny jest niedozwolona.
 */
export const getAllowedProcessorIdsForStage = (stage) => {
  const procNazwaId = getProcesNazwaId(stage);
  return ALLOWED_PROCESSORS_BY_PROCES_NAZWA_ID[procNazwaId] || [];
};

const getEffectiveProcessorId = (stage) =>
  stage?.procesor_id_symulacji !== undefined &&
  stage?.procesor_id_symulacji !== null &&
  stage?.procesor_id_symulacji !== ''
    ? stage.procesor_id_symulacji
    : stage?.procesor_id;

const getShipmentTimestampMs = (dateVal) => {
  if (!dateVal) return NaN;
  const d = new Date(String(dateVal).replace(' ', 'T'));
  if (Number.isNaN(d.getTime())) return NaN;
  const str = String(dateVal).trim();
  if (str.length <= 10 || (d.getHours() === 0 && d.getMinutes() === 0)) {
    d.setHours(6, 0, 0, 0);
  }
  return d.getTime();
};

const formatDateTimePL = (ms) => {
  if (!ms || Number.isNaN(ms)) return '--';
  return new Date(ms).toLocaleString('pl-PL', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatDelayHuman = (delayMs) => {
  const totalMinutes = Math.ceil(Math.abs(delayMs) / 60000);
  const days = Math.floor(totalMinutes / (24 * 60));
  const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
  const mins = totalMinutes % 60;

  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (mins > 0 && days === 0) parts.push(`${mins}m`);
  return parts.join(' ') || '0h';
};

/**
 * Wyznacza graniczną datę rozpoczęcia produkcji dla zamówienia:
 * 0) z nadpisanej wartości użytkownika w sesji (customProdStartLimits)
 * 1) z pola stage.data_rozpoczecia_symulacji (jeśli zapisane)
 * 2) z pola stage.data_rozpoczecia_produkcji (format YYYY-MM-DD HH:mm)
 * 3) jeśli brak -> 5 dni wstecz od data_spedycji_symulacji / data_spedycji (06:00)
 */
export const getOrderProductionStartLimitMs = (
  orderStages = [],
  customProdStartLimits = {}
) => {
  if (orderStages.length > 0) {
    const zamId = String(orderStages[0].zamowienie_id);
    if (customProdStartLimits[zamId] !== undefined) {
      return {
        limitMs: customProdStartLimits[zamId],
        isFallback5Days: false,
        isCustomOverride: true,
      };
    }
  }

  // Najpierw sprawdzamy data_rozpoczecia_symulacji, a potem data_rozpoczecia_produkcji
  for (let i = 0; i < orderStages.length; i++) {
    const rawStartProd =
      orderStages[i].data_rozpoczecia_symulacji ||
      orderStages[i].data_rozpoczecia_produkcji;

    if (
      rawStartProd &&
      String(rawStartProd).trim() !== '' &&
      rawStartProd !== '0000-00-00 00:00:00'
    ) {
      const parsed = new Date(String(rawStartProd).replace(' ', 'T')).getTime();
      if (!Number.isNaN(parsed)) {
        return { limitMs: parsed, isFallback5Days: false, isCustomOverride: false };
      }
    }
  }

  // Fallback: 5 dni wstecz od aktywnej daty spedycji
  for (let i = 0; i < orderStages.length; i++) {
    const rawSped =
      orderStages[i].data_spedycji_symulacji || orderStages[i].data_spedycji;
    if (rawSped) {
      const spedMs = getShipmentTimestampMs(rawSped);
      if (!Number.isNaN(spedMs)) {
        return {
          limitMs: spedMs - FIVE_DAYS_MS,
          isFallback5Days: true,
          isCustomOverride: false,
        };
      }
    }
  }

  return { limitMs: Date.now(), isFallback5Days: true, isCustomOverride: false };
};

/**
 * Szuka najpóźniejszej wolnej luki [start, end] <= maxEndMs.
 */
const findBackwardSlotOnProcessor = (occupiedIntervals, durationMs, maxEndMs) => {
  let candidateEndMs = maxEndMs;
  let candidateStartMs = candidateEndMs - durationMs;

  const sortedOccupied = [...occupiedIntervals].sort((a, b) => b.endMs - a.endMs);
  let changed = true;
  let guard = 0;

  while (changed && guard < 300) {
    changed = false;
    guard++;

    for (let i = 0; i < sortedOccupied.length; i++) {
      const item = sortedOccupied[i];
      if (
        candidateStartMs < item.endMs - TOLERANCE_MS &&
        candidateEndMs > item.startMs + TOLERANCE_MS
      ) {
        candidateEndMs = item.startMs;
        candidateStartMs = candidateEndMs - durationMs;
        changed = true;
        break;
      }
    }
  }

  return { startMs: candidateStartMs, endMs: candidateEndMs };
};

/**
 * Szuka najwcześniejszej wolnej luki w prawo (w przyszłość), zaczynając od minStartMs.
 */
const findEarliestForwardSlotOnProcessor = (
  occupiedIntervals,
  durationMs,
  minStartMs
) => {
  let candidateStartMs = minStartMs;
  let candidateEndMs = candidateStartMs + durationMs;

  const sortedAsc = [...occupiedIntervals].sort((a, b) => a.startMs - b.startMs);
  let changed = true;
  let guard = 0;

  while (changed && guard < 300) {
    changed = false;
    guard++;

    for (let i = 0; i < sortedAsc.length; i++) {
      const item = sortedAsc[i];
      if (
        candidateStartMs < item.endMs - TOLERANCE_MS &&
        candidateEndMs > item.startMs + TOLERANCE_MS
      ) {
        candidateStartMs = item.endMs;
        candidateEndMs = candidateStartMs + durationMs;
        changed = true;
        break;
      }
    }
  }

  return { startMs: candidateStartMs, endMs: candidateEndMs };
};

/**
 * Szuka alternatywnych procesorów wyłącznie w obrębie dozwolonej puli:
 * - proces_nazwa_id == 1 (Druk): procesor_id 1, 2, 3
 * - proces_nazwa_id == 3 (Falcowanie): procesor_id 14, 15, 16, 17, 18, 19, 20, 21
 * W pozostałych przypadkach zwraca pustą tablicę.
 */
const findAlternativeProcessorsForStage = ({
  stage,
  workingStages,
  procesory = [],
  targetProcessorId,
  minOrderStartMs,
  effectiveLimitMs,
}) => {
  const allowedPool = getAllowedProcessorIdsForStage(stage);

  // Jeśli to nie jest druk (1) ani falcowanie (3), nie szukamy innego procesora
  if (!allowedPool || allowedPool.length === 0) {
    return [];
  }

  const candidateProcIds = allowedPool.filter(
    (id) => String(id) !== String(targetProcessorId)
  );

  const alternatives = [];

  candidateProcIds.forEach((altProcId) => {
    // Wszystkie prace aktualnie przypisane w symulacji do tego alternatywnego procesora (bez przerw typ_grupy == 1)
    const altStages = workingStages.filter(
      (s) =>
        s.typ_grupy != 1 &&
        String(getEffectiveProcessorId(s)) === String(altProcId) &&
        s._uid !== stage._uid
    );

    const altOccupied = altStages.map((s) => ({
      uid: s._uid,
      startMs: s._startMs,
      endMs: s._endMs,
    }));

    const altSlot = findBackwardSlotOnProcessor(
      altOccupied,
      stage._durationMs,
      stage._endMs
    );

    const requiredLeftShiftMs = Math.max(0, stage._endMs - altSlot.endMs);
    const projectedOrderStartMs = minOrderStartMs - requiredLeftShiftMs;
    const earliestProjectedMs = Math.min(projectedOrderStartMs, altSlot.startMs);

    // Sprawdzamy, czy na alternatywnym procesorze klocek i całe zamówienie mieszczą się >= effectiveLimitMs
    if (earliestProjectedMs >= effectiveLimitMs - TOLERANCE_MS) {
      const additionalMarginMin = Math.round(requiredLeftShiftMs / 60000);
      const procObj = procesory.find((p) => String(p.id) === String(altProcId));

      alternatives.push({
        procesorId: Number(altProcId),
        procesorNazwa: procObj?.nazwa || `Procesor ${altProcId}`,
        slotStartMs: altSlot.startMs,
        slotEndMs: altSlot.endMs,
        slotStartStr: formatDateTimePL(altSlot.startMs),
        slotEndStr: formatDateTimePL(altSlot.endMs),
        requiredLeftShiftMs,
        additionalMarginMin,
      });
    }
  });

  // Sortujemy od maszyny wymagającej najmniejszego cofnięcia (najbliżej oryginalnego terminu)
  return alternatives.sort((a, b) => a.requiredLeftShiftMs - b.requiredLeftShiftMs);
};

/**
 * Główna funkcja rozsuwająca nachodzące klocki na wskazanym procesorze.
 * Nigdy nie pozwala cofnąć niezakończonej pracy w przeszłość (przed Date.now())
 * ani przed data_rozpoczecia_symulacji / data_rozpoczecia_produkcji.
 */
export const resolveProcessorOverlaps = ({
  calculatedStages = [],
  targetProcessorId,
  currentCustomMargins = {},
  customProdStartLimits = {},
  procesory = [],
  recalculateFn = null,
}) => {
  const nowMs = Date.now();
  let workingStages = calculatedStages.filter((s) => s && s.status != 4);

  // W symulacji bierzemy klocki przypisane do targetProcessorId (z pominięciem przerw typ_grupy == 1, które zostały na Torze 1)
  const initialProcStages = workingStages.filter(
    (s) =>
      s.typ_grupy != 1 &&
      String(getEffectiveProcessorId(s)) === String(targetProcessorId)
  );

  if (initialProcStages.length === 0) {
    return {
      updatedMargins: currentCustomMargins,
      unfitStageUids: [],
      unfitOrdersMap: {},
      suggestions: [],
    };
  }

  const updatedMargins = { ...currentCustomMargins };
  const occupiedIntervals = [];
  const unfitStageUids = [];
  const unfitOrdersMap = {};
  const suggestions = [];

  const fixedStages = initialProcStages.filter((s) => s.zablokowany == 1);
  const movableStageUids = initialProcStages
    .filter((s) => s.zablokowany != 1)
    .sort((a, b) => b._endMs - a._endMs || b._startMs - a._startMs)
    .map((s) => s._uid);

  fixedStages.forEach((s) => {
    occupiedIntervals.push({
      uid: s._uid,
      startMs: s._startMs,
      endMs: s._endMs,
    });
  });

  movableStageUids.forEach((uid) => {
    const stage = workingStages.find((s) => s._uid === uid);
    if (!stage) return;

    const zamId = String(stage.zamowienie_id);
    const orderStages = workingStages.filter(
      (s) => s.typ_grupy != 1 && String(s.zamowienie_id) === zamId
    );
    const minOrderStartMs = Math.min(...orderStages.map((s) => s._startMs));

    const { limitMs: prodStartLimitMs, isFallback5Days } =
      getOrderProductionStartLimitMs(orderStages, customProdStartLimits);

    // Twarda granica: praca NIE MOŻE zacząć się wcześniej niż TERAZ (nowMs)
    // oraz nie może zacząć się wcześniej niż data_rozpoczecia_symulacji / data_rozpoczecia_produkcji
    const effectiveLimitMs =
      prodStartLimitMs !== null ? Math.max(nowMs, prodStartLimitMs) : nowMs;

    // Szukamy najpóźniejszej wolnej luki w lewo od obecnego końca klocka
    const backwardSlot = findBackwardSlotOnProcessor(
      occupiedIntervals,
      stage._durationMs,
      stage._endMs
    );

    const requiredLeftShiftMs = Math.max(0, stage._endMs - backwardSlot.endMs);

    // Gdzie wylądowałby najwcześniejszy proces tego zamówienia po cofnięciu o requiredLeftShiftMs:
    const projectedOrderStartMs = minOrderStartMs - requiredLeftShiftMs;
    const projectedStageStartMs = backwardSlot.startMs;
    const earliestProjectedMs = Math.min(projectedOrderStartMs, projectedStageStartMs);

    // SPRAWDZENIE: Czy po rozsunięciu praca mieści się >= effectiveLimitMs?
    if (earliestProjectedMs >= effectiveLimitMs - TOLERANCE_MS) {
      // MIEŚCI SIĘ NA OBECNYM PROCESORZE!
      const additionalMarginMin = Math.round(requiredLeftShiftMs / 60000);

      if (additionalMarginMin > 0) {
        const prevOverride = updatedMargins[stage._uid] || {
          marginLeft: stage.marginLeft || 0,
          marginRight: stage.marginRight || 0,
        };

        updatedMargins[stage._uid] = {
          ...prevOverride,
          marginRight: (prevOverride.marginRight || 0) + additionalMarginMin,
        };

        if (typeof recalculateFn === 'function') {
          workingStages = recalculateFn(updatedMargins).filter(
            (s) => s && s.status != 4
          );
        }
      }

      occupiedIntervals.push({
        uid: stage._uid,
        startMs: backwardSlot.startMs,
        endMs: backwardSlot.endMs,
      });
    } else {
      // NIE MIEŚCI SIĘ NA TYM PROCESORZE!
      unfitStageUids.push(stage._uid);

      // 1. Szukamy wolnego miejsca na INNYCH dozwolonych procesorach (tylko dla proces_nazwa_id == 1 lub 3)
      const alternativeProcessors = findAlternativeProcessorsForStage({
        stage,
        workingStages,
        procesory,
        targetProcessorId,
        minOrderStartMs,
        effectiveLimitMs,
      });

      // 2. Sprawdzamy, czy praca zmieściłaby się przed TERAZ, gdybyśmy cofnęli jej data_rozpoczecia_symulacji:
      const canFixByMovingProdStart =
        prodStartLimitMs !== null &&
        prodStartLimitMs > nowMs + TOLERANCE_MS &&
        earliestProjectedMs >= nowMs - TOLERANCE_MS;

      const requiredProdStartShiftMs = canFixByMovingProdStart
        ? prodStartLimitMs - earliestProjectedMs
        : null;

      // 3. Wyliczamy propozycję OPÓŹNIENIA DATY SPEDYCJI (w prawo):
      const orderToStageOffsetMs = Math.max(0, stage._startMs - minOrderStartMs);
      const minAllowedStageStartMs = effectiveLimitMs + orderToStageOffsetMs;

      const forwardSlot = findEarliestForwardSlotOnProcessor(
        occupiedIntervals,
        stage._durationMs,
        minAllowedStageStartMs
      );

      const requiredDelayMs = Math.max(
        60 * 1000,
        forwardSlot.endMs - stage._endMs
      );

      const activeSpedStr = stage.data_spedycji_symulacji || stage.data_spedycji;
      const currentSpedMs = getShipmentTimestampMs(activeSpedStr);
      const proposedSpedMs = currentSpedMs + requiredDelayMs;

      // Klocek zostaje na swoim miejscu i świeci na czerwono
      occupiedIntervals.push({
        uid: stage._uid,
        startMs: stage._startMs,
        endMs: stage._endMs,
      });

      const fallsIntoPast = earliestProjectedMs < nowMs - TOLERANCE_MS;

      const suggestionItem = {
        uid: stage._uid,
        zamowienieId: zamId,
        procesorId: getEffectiveProcessorId(stage),
        nr: stage.nr,
        rok: stage.rok,
        firma_nazwa: stage.firma_nazwa,
        tytul: stage.tytul,
        productionStartLimitMs: prodStartLimitMs,
        effectiveLimitMs,
        isFallback5Days,
        fallsIntoPast,
        wouldStartAtMs: earliestProjectedMs,
        // Propozycja A: Przeniesienie na inny procesor (tylko druk [1,2,3] lub falcowanie [14..21])
        alternativeProcessors,
        // Propozycja B: Przesunięcie daty rozpoczęcia produkcji (jeśli >= Teraz)
        canFixByMovingProdStart,
        requiredProdStartShiftMs,
        requiredProdStartShiftHuman: requiredProdStartShiftMs
          ? formatDelayHuman(requiredProdStartShiftMs)
          : null,
        proposedProdStartMs: canFixByMovingProdStart ? earliestProjectedMs : null,
        proposedProdStartStr: canFixByMovingProdStart
          ? formatDateTimePL(earliestProjectedMs)
          : null,
        // Propozycja C: Opóźnienie daty spedycji
        currentSpedycjaStr: activeSpedStr,
        currentSpedMs,
        requiredDelayMs,
        requiredDelayHuman: formatDelayHuman(requiredDelayMs),
        proposedSpedMs,
        proposedSpedDateStr: formatDateTimePL(proposedSpedMs),
        stageObj: stage,
      };

      suggestions.push(suggestionItem);

      if (
        !unfitOrdersMap[zamId] ||
        requiredDelayMs > unfitOrdersMap[zamId].requiredDelayMs
      ) {
        unfitOrdersMap[zamId] = suggestionItem;
      }
    }
  });

  return {
    updatedMargins,
    unfitStageUids,
    unfitOrdersMap,
    suggestions,
  };
};

// --- KOMPONENT OKIENKA Z PROPOZYCJAMI (ZMIANA PROCESORA / START PRODUKCJI / SPEDYCJA) ---

export const GantMultiDelayPanel = ({
  suggestions = [],
  procesory = [],
  onApplyProcessorChange,
  onApplyShipmentDelay,
  onApplyProdStartShift,
  onSelectOrder,
  onClearResolution,
  onClose,
}) => {
  if (!suggestions || suggestions.length === 0) return null;

  return (
    <div
      style={{
        position: 'absolute',
        bottom: '54px',
        right: '14px',
        width: '480px',
        maxHeight: '460px',
        backgroundColor: '#1f2937',
        color: '#f9fafb',
        border: '1px solid #ef4444',
        borderRadius: '8px',
        boxShadow: '0 12px 24px rgba(0, 0, 0, 0.65)',
        zIndex: 120,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        fontSize: '12px',
      }}
    >
      {/* Nagłówek */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '8px 12px',
          backgroundColor: '#7f1d1d',
          borderBottom: '1px solid #991b1b',
          fontWeight: 700,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <AlertTriangle size={16} color="#fca5a5" />
          <span>
            Kolizje dat ({suggestions.length}) – Propozycje zmian
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <button
            onClick={onClearResolution}
            title="Cofnij rozsunięcie i wyczyść czerwone podświetlenia"
            style={{
              background: '#991b1b',
              color: '#fecaca',
              border: '1px solid #dc2626',
              borderRadius: '4px',
              padding: '2px 6px',
              fontSize: '10px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '3px',
            }}
          >
            <RotateCcw size={11} /> Reset
          </button>

          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#fca5a5',
              cursor: 'pointer',
              display: 'flex',
            }}
          >
            <X size={15} />
          </button>
        </div>
      </div>

      {/* Lista niemieszczących się prac */}
      <div
        style={{
          overflowY: 'auto',
          padding: '8px',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px',
        }}
      >
        {suggestions.map((item) => {
          const procName =
            procesory.find((p) => String(p.id) === String(item.procesorId))?.nazwa ||
            `Procesor ${item.procesorId}`;

          return (
            <div
              key={item.uid}
              onClick={() => onSelectOrder?.(item.stageObj)}
              style={{
                backgroundColor: '#111827',
                borderLeft: '4px solid #ef4444',
                borderRadius: '4px',
                padding: '8px 10px',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                gap: '5px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <strong style={{ color: '#fca5a5', fontSize: '12px' }}>
                  {item.nr}_{String(item.rok || '').substring(2, 4)} {item.firma_nazwa}
                </strong>
                <span
                  style={{
                    backgroundColor: '#374151',
                    color: '#e5e7eb',
                    padding: '1px 6px',
                    borderRadius: '4px',
                    fontSize: '10px',
                  }}
                >
                  {procName}
                </span>
              </div>

              <div
                style={{
                  color: '#9ca3af',
                  fontSize: '11px',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {item.tytul}
              </div>

              <div style={{ fontSize: '10px', color: '#d1d5db' }}>
                <div>
                  Blokada startu produkcji:{' '}
                  <strong style={{ color: '#60a5fa' }}>
                    {formatDateTimePL(item.productionStartLimitMs)}
                  </strong>{' '}
                  {item.isFallback5Days
                    ? '(-5 dni od spedycji)'
                    : '(data_rozpoczecia_symulacji / prod.)'}
                </div>
                <div>
                  Wymagany start po cofnięciu:{' '}
                  <strong style={{ color: '#f87171' }}>
                    {formatDateTimePL(item.wouldStartAtMs)}
                  </strong>{' '}
                  {item.fallsIntoPast ? (
                    <span style={{ color: '#ef4444', fontWeight: 700 }}>
                      (wypada w PRZESZŁOŚCI przed Teraz!)
                    </span>
                  ) : (
                    <span>(przed datą rozpoczęcia produkcji)</span>
                  )}
                </div>
              </div>

              {/* PROPOZYCJA A: Przeniesienie na alternatywny procesor (tylko druk [1,2,3] lub falcowanie [14..21]) */}
              {item.alternativeProcessors && item.alternativeProcessors.length > 0 && (
                <div
                  style={{
                    marginTop: '2px',
                    padding: '6px 8px',
                    backgroundColor: '#4c1d9544',
                    border: '1px solid #7c3aed',
                    borderRadius: '4px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '5px',
                  }}
                >
                  <span style={{ color: '#ddd6fe', fontSize: '11px', fontWeight: 600 }}>
                    Wolne maszyny w tej grupie (bez opóźnienia spedycji):
                  </span>

                  {item.alternativeProcessors.map((alt) => (
                    <div
                      key={`alt-${item.uid}-${alt.procesorId}`}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        backgroundColor: '#2e106566',
                        padding: '4px 6px',
                        borderRadius: '4px',
                      }}
                    >
                      <div>
                        <strong style={{ color: '#c4b5fd', fontSize: '11px' }}>
                          {alt.procesorNazwa}
                        </strong>
                        <div style={{ fontSize: '10px', color: '#e9d5ff' }}>
                          Termin: {alt.slotStartStr} – {alt.slotEndStr}
                          {alt.additionalMarginMin > 0
                            ? ` (marginRight +${alt.additionalMarginMin}m)`
                            : ' (bez przesunięcia)'}
                        </div>
                      </div>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onApplyProcessorChange?.(
                            item.uid,
                            item.zamowienieId,
                            alt.procesorId,
                            alt.additionalMarginMin,
                            item.procesorId
                          );
                        }}
                        title={`Przypisz procesor_id_symulacji = ${alt.procesorNazwa}`}
                        style={{
                          backgroundColor: '#7c3aed',
                          color: '#ffffff',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '5px 8px',
                          fontSize: '10px',
                          fontWeight: 600,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                          flexShrink: 0,
                        }}
                      >
                        <ArrowRightLeft size={12} /> Przenieś
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* PROPOZYCJA B: Przyspieszenie daty rozpoczęcia produkcji (tylko gdy nie wpada w przeszłość przed TERAZ) */}
              {item.canFixByMovingProdStart && (
                <div
                  style={{
                    marginTop: '2px',
                    padding: '5px 8px',
                    backgroundColor: '#1e3a8a44',
                    border: '1px solid #1d4ed8',
                    borderRadius: '4px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <div>
                    <span style={{ color: '#93c5fd', fontSize: '11px' }}>
                      Przyspiesz start prod. o:{' '}
                      <strong>-{item.requiredProdStartShiftHuman}</strong>
                    </span>
                    <div style={{ fontSize: '10px', color: '#bfdbfe' }}>
                      Nowy start symulacji: <strong>{item.proposedProdStartStr}</strong>
                    </div>
                  </div>

                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onApplyProdStartShift?.(
                        item.zamowienieId,
                        item.proposedProdStartMs,
                        item.procesorId
                      );
                    }}
                    title="Zapisz wcześniejszą data_rozpoczecia_symulacji i ułóż pracę w wolnej luce"
                    style={{
                      backgroundColor: '#2563eb',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '5px 8px',
                      fontSize: '10px',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      flexShrink: 0,
                    }}
                  >
                    <CalendarClock size={13} /> Cofnij start prod.
                  </button>
                </div>
              )}

              {/* PROPOZYCJA C: Opóźnienie daty spedycji */}
              <div
                style={{
                  marginTop: '2px',
                  padding: '5px 8px',
                  backgroundColor: '#064e3b44',
                  border: '1px solid #047857',
                  borderRadius: '4px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <div>
                  <span style={{ color: '#fbbf24', fontSize: '11px' }}>
                    Opóźnij spedycję o: <strong>+{item.requiredDelayHuman}</strong>
                  </span>
                  <div style={{ fontSize: '10px', color: '#34d399' }}>
                    Nowa spedycja symulacji: <strong>{item.proposedSpedDateStr}</strong>
                  </div>
                </div>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onApplyShipmentDelay?.(item.zamowienieId, item.requiredDelayMs);
                  }}
                  title="Zasymuluj opóźnienie spedycji tego zlecenia (data_spedycji_symulacji)"
                  style={{
                    backgroundColor: '#059669',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '5px 8px',
                    fontSize: '10px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    flexShrink: 0,
                  }}
                >
                  <CalendarPlus size={13} /> Opóźnij spedycję
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};